import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ContractChangesComponent } from "@app/views/contract-changes/contract-changes.component";
import { OkDialogComponent } from "@app/views/ok-dialog-component/ok-dialog.component";
import { PersonalDataComponent } from "@app/views/personal-data/personal-data.component";
import { TranslateModule } from "@ngx-translate/core";
import { HttpErrors } from "@shared/enums/http-errors-type.enum";
import { convertDateToUTC } from "@shared/helpers/date.helper";
import { Country } from "@shared/models/country";
import { Member } from "@shared/models/member";
import { MemberUpdateCommand } from "@shared/models/member-update-command.model";
import { PhonePrefixes } from "@shared/models/phone-prefixes";
import { PolicyChangeLog } from "@shared/models/policy-change-log.model";
import { PolicyFieldsVM } from "@shared/models/policy-field-vm.model";
import { CountryService } from "@shared/services/country.service";
import { MembersService } from "@shared/services/members.service";
import { PhonePrefixesService } from "@shared/services/phone-prefixes.service";
import { PolicyRecoveryService } from "@shared/services/policy-recovery.service";
import { PolicyService } from "@shared/services/policy.service";
import _ from "lodash";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { StepperModule } from "primeng/stepper";
import { fromDateTimeToDateOnly } from "@shared/helpers/date.helper";

@Component({
  selector: "app-profil-edition",
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    PersonalDataComponent,
    ContractChangesComponent,
    DialogModule,
    StepperModule,
    ButtonModule,
    OkDialogComponent,
  ],
  templateUrl: "./profil-edition.component.html",
  styleUrl: "./profil-edition.component.scss",
})
export class ProfilEditionComponent implements OnInit {
  @Input() visibleDialog!: boolean;
  @Output() dialogEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() emitUpdateValidation: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild(PersonalDataComponent) personalDataComponent!: PersonalDataComponent;

  readonly defaultPhoneMask = "9 99 99 99 99";
  policyFieldsVM!: PolicyFieldsVM;
  phonePrefixes!: PhonePrefixes[];
  member!: Member;
  memberCopy!: Member;
  policyChangeLog!: PolicyChangeLog;
  activeIndex = 0;
  selectedSignaturePhoneNumber!: string;
  selectedPhonePrefix!: PhonePrefixes | undefined;
  selectedSignaturePhonePrefix!: PhonePrefixes;
  birthCountryId!: number;
  birthZipCode!: string;
  nationalityId!: number;
  phoneMask = this.defaultPhoneMask;
  displaySignaturePhone = false;
  savePhone = false;
  isFormValid: boolean[] = [true, true];
  selectedPhoneMask!: string;
  previousStep = 0;
  IsCallInErrorDialogVisible = false;
  IsSaveDialogVisible = false;
  countriesOptions: Country[] = [];
  contratTarget = "contrat";
  hasMemberMultipleReferences = false;
  hasEndorsementWait4Sign = false;
  personalDataHasChanged = false;

  constructor(
    private readonly policyService: PolicyService,
    private readonly memberService: MembersService,
    private readonly phonePrefixesService: PhonePrefixesService,
    private readonly countryService: CountryService,
    private readonly policyRecoveryService: PolicyRecoveryService
  ) {}

  ngOnInit(): void {
    this.setPolicyFields();
    this.setMemberInformations();
    this.setCountries();
    this.setHasEndorsementWait4Sign();
  }

  setHasEndorsementWait4Sign() {
    this.policyService.getPolicyWait4SignEndorsement().subscribe({
      next: policies => {
        this.hasEndorsementWait4Sign = policies.length > 0;
      },
    });
  }

  setCountries() {
    this.countryService.getCountries().subscribe(data => {
      this.countriesOptions = data;
    });
  }

  setPolicyFields() {
    this.policyService.getPolicies().subscribe(data => {
      if (data.length === 0) {
        return;
      }
      this.policyService.getPolicyFields(data[0].id).subscribe(data => {
        this.policyFieldsVM = data;
      });
    });
  }

  setMemberInformations() {
    this.memberService.getMemberInformations().subscribe(data => {
      this.member = data;
      this.selectedSignaturePhoneNumber = this.member.phoneNumber;
      const parsedMember = JSON.parse(JSON.stringify(this.member));
      if (parsedMember.birthDate) {
        parsedMember.birthDate = new Date(parsedMember.birthDate);
      }
      this.memberCopy = parsedMember;
      this.memberService.getMemberReferences(this.memberCopy.id).subscribe(result => {
        this.hasMemberMultipleReferences = result.filter(x => x.target === this.contratTarget).length > 1;
      });
      this.setPhonePrefixes();
    });
  }

  setPhonePrefixes() {
    this.phonePrefixesService.getPhonePrefixes().subscribe(data => {
      this.phonePrefixes = data;
      this.selectedPhonePrefix = this.phonePrefixes.find(x => x.prefix === this.member.phonePrefix);
      this.selectedSignaturePhonePrefix =
        this.phonePrefixes.find(x => x.prefix === this.member.phonePrefix) || this.phonePrefixes[0];
    });
  }

  onFormValidityChange($event: number) {
    this.setIsFormValid($event);
  }

  onPhoneMaskChange($event: string) {
    this.phoneMask = $event;
  }

  onCountryHasChange(event: number) {
    this.birthCountryId = event;
  }

  onNationalityHasChange(event: number) {
    this.nationalityId = event;
  }

  onPhoneNumberHasChange(event: string) {
    this.selectedSignaturePhoneNumber = event;
  }

  onPhonePrefixHasChange(event: PhonePrefixes) {
    this.selectedPhonePrefix = event;
    this.selectedSignaturePhonePrefix = event;
  }

  onSignaturePhonePrefixChange(event: { phonePrefix: PhonePrefixes; phoneMask: string }) {
    this.selectedSignaturePhonePrefix = event.phonePrefix;
    this.selectedPhoneMask = event.phoneMask;
    this.setIsFormValid(2);
  }

  onSignaturePhoneNumberChange(phoneNumber: string) {
    this.selectedSignaturePhoneNumber = phoneNumber;
    this.setIsFormValid(2);
  }

  onSignaturePhoneSignatureCheckBoxChange(checked: boolean) {
    this.savePhone = checked;
    this.setIsFormValid(2);
  }

  setIsFormValid(step: number) {
    if (!this.memberCopy) {
      return;
    }
    step--;
    //Personnal

    if (step === 0) {
      this.isFormValid[step] =
        this.memberCopy.firstName !== "" &&
        this.memberCopy.lastName !== "" &&
        this.memberCopy.nationality !== "" &&
        this.memberCopy.birthZipCode !== "" &&
        this.memberCopy.birthCity !== "" &&
        this.memberCopy.birthCountry !== "" &&
        this.memberCopy.zipCode !== "" &&
        this.memberCopy.address1 !== "" &&
        this.memberCopy.addressCity !== "";
    }

    //Changes
    if (step === 1) {
      this.isFormValid[step] = this.displaySignaturePhone
        ? this.selectedSignaturePhonePrefix !== null &&
          this.selectedSignaturePhoneNumber !== null &&
          this.selectedSignaturePhoneNumber !== "" &&
          this.selectedSignaturePhoneNumber?.length === this.selectedPhoneMask.replace(/\s/g, "").length
        : true;
    }
  }

  hasChanged() {
    this.checkPersonalDataChanged();
    return this.personalDataHasChanged;
  }

  checkPersonalDataChanged() {
    if (this.personalDataComponent) {
      this.personalDataHasChanged =
        this.personalDataComponent?.cityHasChanged ||
        this.personalDataComponent?.emailHasChanged ||
        this.personalDataComponent?.zipCodeHasChanged ||
        this.personalDataComponent?.address1HasChanged ||
        this.personalDataComponent?.address2HasChanged ||
        this.personalDataComponent?.civilityHasChanged ||
        this.personalDataComponent?.lastNameHasChanged ||
        this.personalDataComponent?.birthCityHasChanged ||
        this.personalDataComponent?.birthCountryHasChanged ||
        this.personalDataComponent?.phoneNumberHasChanged ||
        this.personalDataComponent?.phonePrefixHasChanged ||
        this.personalDataComponent?.emailConsentHasChanged ||
        this.personalDataComponent?.smsConsentHasChanged ||
        this.personalDataComponent?.memberBirthDateHasChanged ||
        this.personalDataComponent?.nationalityHasChanged ||
        this.personalDataComponent?.birthZipCodeHasChanged ||
        this.personalDataComponent?.firstNameHasChanged;
    }
  }

  updateMember() {
    const computeMemberUpdateCommand = this.toComputeMemberUpdateCommand();
    this.memberService.updateMemberChange(computeMemberUpdateCommand).subscribe({
      next: () => {
        this.redirect();
      },
      error: () => {
        this.IsCallInErrorDialogVisible = true;
        this.emitUpdateValidation.emit(false);
      },
    });
  }

  redirect() {
    this.policyService.getPolicyWait4SignEndorsement().subscribe({
      next: policies => {
        if (policies.length > 0) {
          this.policyRecoveryService.getSignatureUrls(policies);
        } else {
          this.emitUpdateValidation.emit(true);
        }
        this.close();
      },
      error: () => {
        this.IsCallInErrorDialogVisible = true;
      },
    });
  }

  computeChanges() {
    this.computeSignatureData();
    const computeMemberUpdateCommand = this.toComputeMemberUpdateCommand();
    this.memberService.computeMemberUpdate(computeMemberUpdateCommand).subscribe({
      next: (data: PolicyChangeLog) => {
        this.policyChangeLog = data;
        this.previousStep = this.activeIndex;
        this.activeIndex++;
      },
      error: resContract => {
        if (resContract.error?.status === HttpErrors.BadRequest) {
          this.IsCallInErrorDialogVisible = true;
        }
      },
    });
  }

  computeSignatureData() {
    if (
      (_.isNil(this.member.phonePrefix) && _.isNil(this.member.phoneNumber)) ||
      this.member.phoneNumber !== this.selectedSignaturePhoneNumber ||
      this.member.phonePrefix !== this.selectedSignaturePhonePrefix.prefix
    ) {
      this.displaySignaturePhone = true;
    }
  }

  toComputeMemberUpdateCommand(): MemberUpdateCommand {
    if (!this.birthCountryId) {
      this.birthCountryId = this.countriesOptions.find(x => x.countryNameFr === this.memberCopy.birthCountry)!.id;
    }
    if (!this.nationalityId) {
      this.nationalityId = this.countriesOptions.find(x => x.countryNameFr === this.memberCopy.nationality)!.id;
    }

    return {
      memberId: this.member.id,
      signatureData: {
        phonePrefix: this.selectedSignaturePhonePrefix.prefix,
        cellPhone: this.selectedSignaturePhoneNumber,
        savePhone: this.savePhone,
      },
      memberData: {
        birthZipCode: this.memberCopy.birthZipCode,
        nationality: this.nationalityId,
        civilityTypeId: this.memberCopy.civilityId,
        lastName: this.memberCopy.lastName,
        firstName: this.memberCopy.firstName,
        birthCity: this.memberCopy.birthCity,
        birthCountry: this.birthCountryId,
        birthDate: fromDateTimeToDateOnly(this.memberCopy.birthDate),
        address1: this.memberCopy.address1,
        address2: this.memberCopy.address2,
        zipCode: this.memberCopy.zipCode,
        city: this.memberCopy.addressCity,
        email: this.memberCopy.email,
        phonePrefix: this.selectedPhonePrefix?.prefix,
        cellPhone: this.memberCopy.phoneNumber,
        gdaTypeId: this.memberCopy.gdaTypeId === 0 ? null : this.memberCopy.gdaTypeId,
        isDematerializedPreferred: this.memberCopy.isDematerializationPreferred,
        isEmailConsents: this.memberCopy.isEmailConsents,
        isSmsConsents: this.memberCopy.isSmsConsents,
      },
    };
  }

  validateStep() {
    if (this.activeIndex === 0) {
      this.computeChanges();
    }
    if (this.activeIndex === 1) {
      this.updateMember();
      this.previousStep = this.activeIndex;
    }
  }
  cancelStep() {
    this.previousStep = this.activeIndex;
    if (this.activeIndex === 0) {
      this.IsSaveDialogVisible = true;
    } else {
      this.activeIndex--;
    }
  }

  onCallInErrorDialogVisible() {
    this.IsCallInErrorDialogVisible = false;
  }

  onSaveDialogClose(event: boolean) {
    this.IsSaveDialogVisible = false;
    if (event) {
      this.close();
    }
  }

  close() {
    this.dialogEmitter.emit(false);
  }
}
